import Hotjar from '@hotjar/browser';
import PropTypes from 'prop-types';
import React, {
    lazy,
    Suspense,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactGA from 'react-ga4';
import {
    Navigate,
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { injectStyle } from 'react-toastify/dist/inject-style';

import AuthPopupCallbackPage from '@views/AuthPopupCallbackPage';
import { defineAbility } from '../../auth/authHelpers';
import { AbilityContext } from '../../auth/Can';
import * as routes from '../../constants/routes';
import {
    ALIGN,
    COLORS,
    POL_THEME,
    ThemeContext,
} from '../../constants/theme.ts';
import { AppContext } from '../../contexts/AppContext';
import { genCanonicalLink } from '../../utils/canonical';
import {
    usePrevious,
    useWindowSize,
} from '../../utils/hooks';

// Most important components loaded up front
import Error404 from '../error-404/404-error';
import Error500 from '../error-500/500-error';
import Footer from '../footer/Footer';
import PublicationPage from '../../views/OrganizationListingPage/ViewBulletin/index.tsx';
import Loader from '../shared/Loader';

import { setMinHeight } from './webpageHeight';

import styles from './AppContainer.scss';
import AppHeader from '../typed/AppHeader/index.tsx';
import { useAppAuth } from '../../auth/useAppAuth.ts';

const LandingPage = lazy(() => import('../../views/LandingPage/index.tsx'));
const About = lazy(() => import('../about/About'));
const Advertise = lazy(() => import('../advertise/Advertise'));
const AdvertisementForm = lazy(() => import('../AdvertisementForm/AdvertisementForm'));
const ExpandedEmailContent = lazy(() => import('../expanded-email-content/ExpandedEmailContent'));
const Help = lazy(() => import('../help/Help'));
const ListingRedirect = lazy(() => import('../listing-redirect/ListingRedirect'));
const OrganizationListingPage = lazy(() => import('../../views/OrganizationListingPage/index.tsx'));
const PublicationWidget = lazy(() => import('../publication-widget/PublicationWidget'));
const SearchResultsPage = lazy(() => import('../../views/SearchResultsPage/index.tsx'));
const AdminSettingsContainer = lazy(() => import('../settings/admin/AdminSettingsContainer'));
const OwnerSettingsContainer = lazy(() => import('../settings/owner/OwnerSettingsContainer'));
const SupporterListingPage = lazy(() => import('../../views/SupporterListingPage/index.tsx'));
const SupportersPage = lazy(() => import('../supporters-page/SupportersContainer'));
const TermsConditions = lazy(() => import('../terms-conditions/TermsConditions'));
const MassTimesUpdateForm = lazy(() => import('../mass-times/MassTimesUpdateForm'));

// Toastify styles
injectStyle();

const AppContainer = () => {
    const location = useLocation();
    const path = location.pathname;
    const { pageTitle, pageDescription } = useContext(AppContext);
    const prevPageTitle = usePrevious(pageTitle);

    const isLandingPage = path === routes.HOME || path === routes.SUPPORTERS;
    const widgetPage = path === routes.PUBLICATION_WIDGET;

    const themeContext = useContext(ThemeContext);

    // Hotjar tracking code
    useEffect(() => {
        if (process.env.PUBLISH_ENV === 'production' && themeContext === POL_THEME) {
            const siteId = 4951827;
            const hotjarVersion = 6;

            Hotjar.init(siteId, hotjarVersion);
        }
    }, [themeContext]);

    useEffect(() => {
        // Trigger pageview if title is not default value and has changed
        if (
            pageTitle !== 'Online Directory'
            && pageTitle !== prevPageTitle
        ) {
            document.title = pageTitle;

            const currentDomain = `${themeContext === POL_THEME ? 'parishes' : 'mycommunity'}online`;
            const canonicalLink = genCanonicalLink(currentDomain, location.pathname, location.search);
            const canonicalTag = document.querySelector('link[rel="canonical"]');
            canonicalTag.setAttribute('href', canonicalLink);

            const metaDescriptionTag = document.querySelector('meta[name="description"]');
            metaDescriptionTag.setAttribute('content', pageDescription);

            ReactGA.send({
                hitType: 'pageview',
                page: location.pathname,
                title: pageTitle,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTitle]);

    // Set page height using window width to handle CLS issues on different pages
    const [pageHeight, setPageHeight] = useState('100vh');
    const windowWidth = useWindowSize();

    useEffect(() => {
        setPageHeight(setMinHeight(location.pathname, location.search, windowWidth));
    }, [location, windowWidth]);

    const appContainerRef = useRef();
    const { user } = useAppAuth();

    // Render Widget
    if (widgetPage) {
        return (
            <div className={styles.widgetDiv}>
                <ToastContainer position="bottom-center" theme="dark" />
                <Suspense fallback={<Loader color={COLORS.secondary} align={ALIGN.center} />}>
                    <PublicationWidget />
                </Suspense>
            </div>
        );
    }

    // Render main website

    // The ToastContainer is in the same div as the AppBar so that the ToastContainer
    // does not take up space in the main div
    return (
        <AbilityContext.Provider value={defineAbility(user)}>
            <div className={styles.appDiv}>
                <div
                    className={styles.container}
                    ref={appContainerRef}
                    style={{
                        minHeight: pageHeight,
                    }}
                >
                    <div className={styles.appBarContainer}>
                        <AppHeader isLandingPage={isLandingPage} />
                        <ToastContainer position="bottom-center" theme="dark" />
                    </div>
                    <Suspense fallback={<Loader color={COLORS.secondary} align={ALIGN.center} />}>
                        <Routes key={user?.actorID || 'anonymous'}>
                            <Route path={routes.HOME} element={<LandingPage />} />
                            <Route path={routes.SUPPORTERS} element={<LandingPage isSupporterHome />} />
                            <Route path={routes.ORGANIZATION_SUPPORTERS_PAGE} element={<SupportersPage />} />
                            <Route path={routes.ORGANIZATION_EMAIL_CONTENT_PAGE} element={<ExpandedEmailContent />} />
                            <Route path={routes.SEARCH} element={<SearchResultsPage />} />
                            <Route path={routes.TERMSCONDITIONS} element={<TermsConditions />} />
                            <Route path={routes.ABOUT} element={<About />} />
                            <Route path={routes.ADVERTISE} element={<Advertise />} />
                            <Route path={routes.HELP} element={<Help />} />
                            <Route path={routes.ERROR_404} element={<Error404 />} />
                            <Route path={routes.FIND_SUPPORTERS_PAGE} element={<ListingRedirect supportersPage />} />
                            <Route path={routes.ERROR_500} element={<Error500 />} />
                            <Route path={routes.FIND_BULLETIN_PAGE} element={<PublicationPage />} />
                            <Route path={routes.FIND} element={<ListingRedirect />} />
                            <Route path={routes.ORGANIZATION_BULLETIN_PAGE} element={<PublicationPage />} />
                            <Route path={routes.PUBLICATION_PAGE} element={<PublicationPage />} />
                            <Route path={routes.ORGANIZATION_DETAIL_SUBSCRIBE} element={<OrganizationListingPage isSubscribePath />} />
                            <Route path={routes.ORGANIZATION_DETAIL_UNSUBSCRIBE} element={<OrganizationListingPage isUnsubscribePath />} />
                            <Route path={routes.ORGANIZATION_DETAIL} element={<OrganizationListingPage />} />
                            <Route path={routes.SPONSOR_DETAIL} element={<SupporterListingPage />} />
                            <Route path={routes.SETTINGS_ADMIN} element={<PrivateRoute component={<AdminSettingsContainer />} />} />
                            <Route path={routes.SETTINGS_ORG} element={<PrivateRoute component={<OwnerSettingsContainer />} />} />
                            <Route path={routes.SETTINGS_SPONSOR} element={<PrivateRoute component={<OwnerSettingsContainer isSponsor />} />} />
                            <Route path={routes.ADVERTISEMENT_FORM} element={<AdvertisementForm appContainerRef={appContainerRef} />} />
                            <Route path={routes.MASS_TIMES_UPDATE_FORM} element={<MassTimesUpdateForm appContainerRef={appContainerRef} />} />
                            <Route path={routes.LOGIN_REDIRECT} element={<AuthPopupCallbackPage />} />
                            <Route path={routes.LOGIN_POPUP} element={<AuthPopupCallbackPage />} />
                            <Route path="/" element={<Navigate to={routes.HOME} />} />
                            <Route path="*" element={<Navigate to={routes.ERROR_404} />} />
                        </Routes>
                    </Suspense>
                    <Footer />
                </div>
            </div>
        </AbilityContext.Provider>
    );
};

const PrivateRoute = ({ component }) => {
    const {
        isAuthenticated,
        login,
        isLoading,
    } = useAppAuth();
    const hasStartedLoginRef = useRef(false);

    useEffect(() => {
        if (isLoading || isAuthenticated || hasStartedLoginRef.current) {
            return;
        }

        hasStartedLoginRef.current = true;

        login().catch(() => {
            hasStartedLoginRef.current = false;
        });
    }, [
        isAuthenticated,
        isLoading,
        login,
    ]);

    return isAuthenticated ? component : null;
};

PrivateRoute.propTypes = {
    component: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.node),
    ]).isRequired,
};

export default AppContainer;
